import { hash1, hash2, hashToSignedFloat, lerp, smootherstep } from "./math";

// Generate one-dimensional noise against x as the basis
export const noise1D = (
  x: number,
  seed: number,
  smooth: (t: number) => number = smootherstep,
): number => {
  const x0 = Math.floor(x);
  const x1 = x0 + 1;

  const t = x - x0;
  const s = smooth(t);

  const v0 = hashToSignedFloat(hash1(x0, seed));
  const v1 = hashToSignedFloat(hash1(x1, seed));

  return lerp(v0, v1, s);
};

// Generate two-dimensional noise against x,y as the basis
export const noise2D = (
  x: number,
  y: number,
  seed: number,
  smooth: (t: number) => number = smootherstep,
): number => {
  const x0 = Math.floor(x);
  const y0 = Math.floor(y);
  const x1 = x0 + 1;
  const y1 = y0 + 1;

  const tx = x - x0;
  const ty = y - y0;

  const sx = smooth(tx);
  const sy = smooth(ty);

  const v00 = hashToSignedFloat(hash2(x0, y0, seed));
  const v10 = hashToSignedFloat(hash2(x1, y0, seed));
  const v01 = hashToSignedFloat(hash2(x0, y1, seed));
  const v11 = hashToSignedFloat(hash2(x1, y1, seed));

  const ix0 = lerp(v00, v10, sx);
  const ix1 = lerp(v01, v11, sx);

  return lerp(ix0, ix1, sy);
};

export type FbmOptions = {
  // How many layers of noise are stacked together on top of one another
  // Octave 1 would be the broad, overall shape
  // Octave 2 would be medium bumps
  // Octave 3 would be smaller bumps, etc
  // Adding more octaves results in a finer overall level of detail
  octaves?: number;
  // How much the frequency increases each octave
  // For example, if we start out with lacunarity at 2:
  // Octave 1 frequency: 1
  // Octave 2 frequency: 2
  // Octave 3 frequency: 4, etc
  // Higher lacunarity makes finer details appear faster
  lacunarity?: number;
  // How strong each smaller-scale octave is, compared to the previous one
  // For example, if we start out with gain 0.5:
  // Octave 1 amplitude: 1
  // Octave 2 amplitude: 0.5
  // Octave 3 amplitude: 0.25, etc
  // The bigger the gain is, the less noticeable the changes in each octave
  // A low gain results in a smoother shape, dominated by bigger forms (i.e. lower octaves)
  // A high gain results in a rougher shape with more fine detail
  gain?: number;
};

export const fbm1D = (
  x: number,
  seed: number,
  options: FbmOptions = {},
): number => {
  const {
    octaves = 5,
    lacunarity = 2,
    gain = 0.5,
  } = options;

  let sum = 0;
  let amp = 1;
  let freq = 1;
  let norm = 0;

  for (let i = 0; i < octaves; i += 1) {
    sum += noise1D(x * freq, seed + i * 1013) * amp;
    norm += amp;
    freq *= lacunarity;
    amp *= gain;
  }

  return norm > 0 ? sum / norm : 0;
};

export const fbm2D = (
  x: number,
  y: number,
  seed: number,
  options: FbmOptions = {},
): number => {
  const {
    octaves = 5,
    lacunarity = 2,
    gain = 0.5,
  } = options;

  let sum = 0;
  let amp = 1;
  let freq = 1;
  let norm = 0;

  for (let i = 0; i < octaves; i += 1) {
    sum += noise2D(x * freq, y * freq, seed + i * 1013) * amp;
    norm += amp;
    freq *= lacunarity;
    amp *= gain;
  }

  return norm > 0 ? sum / norm : 0;
};

export const ridge1D = (
  x: number,
  seed: number,
  options: FbmOptions = {},
): number => {
  const n = fbm1D(x, seed, options);
  return 1 - Math.abs(n);
};

export const ridge2D = (
  x: number,
  y: number,
  seed: number,
  options: FbmOptions = {},
): number => {
  const n = fbm2D(x, y, seed, options);
  return 1 - Math.abs(n);
};

export const turbulence2D = (
  x: number,
  y: number,
  seed: number,
  options: FbmOptions = {},
): number => {
  const {
    octaves = 5,
    lacunarity = 2,
    gain = 0.5,
  } = options;

  let sum = 0;
  let amp = 1;
  let freq = 1;
  let norm = 0;

  for (let i = 0; i < octaves; i += 1) {
    sum += Math.abs(noise2D(x * freq, y * freq, seed + i * 1013)) * amp;
    norm += amp;
    freq *= lacunarity;
    amp *= gain;
  }

  return norm > 0 ? sum / norm : 0;
};

// Warping distorts the x,y coordinates of noise sampling
// Using this prevents the noise from looking regular or ordered
export type DomainWarpOptions = {
  // Amplitude controls how big each instance of distortion is
  // Higher amplitude leads to swirlier / more distorted patches
  warpAmp?: number;
  // Frequency controls how often the points are distorted
  // Low frequency leads to larger-scale distortion
  // High frequency leads to more turbulent and smaller scale distortion
  warpFreq?: number;
  fbm?: FbmOptions;
};

export const warpedFbm2D01 = (
  x: number,
  y: number,
  seed: number,
  options: DomainWarpOptions = {},
): number => {
  const {
    warpAmp = 0.75,
    warpFreq = 1,
    fbm: fbmOptions = {},
  } = options;

  const qx = fbm2D(x * warpFreq + 17.1, y * warpFreq + 91.7, seed + 10007, fbmOptions);
  const qy = fbm2D(x * warpFreq - 43.2, y * warpFreq - 28.4, seed + 20011, fbmOptions);

  return fbm2D01(x + qx * warpAmp, y + qy * warpAmp, seed, fbmOptions);
};

export const noise1D01 = (x: number, seed: number): number =>
  noise1D(x, seed) * 0.5 + 0.5;

export const noise2D01 = (x: number, y: number, seed: number): number =>
  noise2D(x, y, seed) * 0.5 + 0.5;

export const fbm2D01 = (
  x: number,
  y: number,
  seed: number,
  options: FbmOptions = {},
): number => fbm2D(x, y, seed, options) * 0.5 + 0.5;